'use client';

import { Routes } from '@/src/routes/routes';
import { Link, useRouter } from '@/src/i18n/navigation';
import { ReactNode, FC, memo, useState, Fragment } from 'react';
import { DestinationTypes, IDestination } from '../services/types';
import {
  useDestinationProduct,
  useDestinationProducts,
  useDestinationStore,
  useDestinationStores,
} from '../services/clientService';
import { cn } from '@/src/lib/utils/command';
import useCmsStore from '@/src/lib/state-stores/cmsStore';

type Props = {
  destination: IDestination;
  children: ReactNode;
  branchId?: number;
};

const ProductDestination: FC<Props> = memo(({ children, destination }) => {
  const router = useRouter();

  const { refetch, isFetching } = useDestinationProduct(
    destination.DestinationValue.toString(),
    false,
  );

  const handleClick = async () => {
    const { data } = await refetch();

    if (!data) return;

    router.push(
      Routes.productDetails(
        data.branchId,
        data.storeNameEnglish,
        data.productId,
        data.productNameEnglish,
      ),
    );
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'focus-visible:ring-primary relative h-full w-full cursor-pointer focus-visible:ring-2 focus-visible:outline-hidden',
        isFetching && 'pointer-events-none opacity-50',
      )}
    >
      {children}
    </div>
  );
});

ProductDestination.displayName = 'ProductDestination';

const StoreDestination: FC<Props> = memo(({ children, destination }) => {
  const router = useRouter();

  const { refetch, isFetching } = useDestinationStore(
    destination.DestinationValue.toString(),
    false,
  );

  const handleClick = async () => {
    const { data } = await refetch();

    if (!data) return;

    router.push(Routes.branchDetails(data[0].store.id, data[0].store.englishName));
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'focus-visible:ring-primary relative h-full w-full cursor-pointer focus-visible:ring-2 focus-visible:outline-hidden',
        isFetching && 'pointer-events-none opacity-50',
      )}
    >
      {children}
    </div>
  );
});
StoreDestination.displayName = 'StoreDestination';

const WebViewDestination: FC<Props> = memo(({ children, destination }) => (
  <Link
    href={`/${Routes.explorPage(destination.EnglishDestinationHeader ?? 'explorer', destination.DestinationValue.toString())}`}
  >
    {children}
  </Link>
));
WebViewDestination.displayName = 'WebViewDestination';

const BrowserDestination: FC<Props> = memo(({ children, destination }) => (
  <Link href={destination.DestinationValue.toString()} target="_blank" rel="noopener noreferrer">
    <span className="sr-only">Open in browser</span>
    {children}
  </Link>
));
BrowserDestination.displayName = 'BrowserDestination';

const IndustryDestination: FC<Props> = memo(({ children }) => <>{children}</>);
IndustryDestination.displayName = 'IndustryDestination';

const SubCategoryDestination: FC<Props> = memo(({ children }) => <>{children}</>);
SubCategoryDestination.displayName = 'SubCategoryDestination';

const InAppNavigationDestination: FC<Props> = memo(({ children }) => <>{children}</>);
InAppNavigationDestination.displayName = 'InAppNavigationDestination';

const NoDestination: FC<Props> = memo(({ children }) => <>{children}</>);
NoDestination.displayName = 'NoDestination';

const RenderListOfProducts: FC<Props> = memo(({ children, destination, branchId }) => {
  const router = useRouter();
  const { updateDestinationValue, updateDestination } = useCmsStore();

  const { refetch, isFetching } = useDestinationProducts(
    destination.DestinationValue as string[],
    false,
    branchId,
  );

  const handleClick = async () => {
    const { data } = await refetch();

    if (!data) return;

    updateDestinationValue(data);
    updateDestination(destination.DestinationType);

    router.push(Routes.customListOfBranches);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'focus-visible:ring-primary relative h-full w-full cursor-pointer focus-visible:ring-2 focus-visible:outline-hidden',
        isFetching && 'pointer-events-none opacity-50',
      )}
    >
      {children}
    </div>
  );
});
RenderListOfProducts.displayName = 'RenderListOfProducts';

const RenderListOfStores: FC<Props> = memo(({ children, destination }) => {
  const router = useRouter();
  const { updateDestinationValue, updateDestination } = useCmsStore();

  const { refetch, isFetching } = useDestinationStores(
    destination.DestinationValue as string[],
    false,
  );

  const handleClick = async () => {
    const { data } = await refetch();

    if (!data) return;

    updateDestinationValue(data);
    updateDestination(destination.DestinationType);
    router.push(Routes.customListOfBranches);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'focus-visible:ring-primary relative h-full w-full cursor-pointer focus-visible:ring-2 focus-visible:outline-hidden',
        isFetching && 'pointer-events-none opacity-50',
      )}
    >
      {children}
    </div>
  );
});
RenderListOfStores.displayName = 'RenderListOfStores';

const DESTINATION_MAP: Record<DestinationTypes, FC<Props>> = {
  [DestinationTypes.PRODUCT]: ProductDestination,
  [DestinationTypes.STORE]: StoreDestination,
  [DestinationTypes.LIST_OF_PRODUCTS]: RenderListOfProducts,
  [DestinationTypes.INDUSTRY]: IndustryDestination,
  [DestinationTypes.NO_DESTINATION]: NoDestination,
  [DestinationTypes.LIST_OF_BRANCHES]: RenderListOfStores,
  [DestinationTypes.PAGE]: WebViewDestination,
  [DestinationTypes.EXTERNAL_URL]: BrowserDestination,
  [DestinationTypes.IN_APP_NAVIGATION]: InAppNavigationDestination,
};

const RenderDestination: FC<Props> = memo(({ destination, children, branchId }) => {
  const Component = DESTINATION_MAP[destination.DestinationType] ?? NoDestination;
  return (
    <Component destination={destination} branchId={branchId}>
      {children}
    </Component>
  );
});
RenderDestination.displayName = 'RenderDestination';

export default RenderDestination;
